import React from 'react'
import { format } from 'date-fns'
import type { Session, Team, Step } from '../../types'
import styles from './ProgressTimeline.module.css'

export interface ProgressTimelineProps {
  session: Session
  teams: Team[]
  steps: Step[]
  className?: string
}

export function ProgressTimeline({
  session,
  teams: _teams,
  steps,
  className,
}: ProgressTimelineProps) {
  const plannedStart = new Date(session.planned_start)
  const plannedEnd = new Date(session.planned_end)
  const plannedStartTime = plannedStart.getTime()
  const plannedEndTime = plannedEnd.getTime()

  const actualEndTime = session.actual_end ? new Date(session.actual_end).getTime() : null
  const nowTime = actualEndTime || Date.now()

  // Overtime if current time or actual completion passed planned end
  const isOvertime = nowTime > plannedEndTime

  const totalDuration = Math.max(1, plannedEndTime - plannedStartTime)
  const elapsed = Math.max(0, nowTime - plannedStartTime)
  const percent = Math.min(100, Math.max(0, Math.round((elapsed / totalDuration) * 100)))

  const stepsDone = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length
  const totalSteps = steps.length

  const startLabel = !isNaN(plannedStartTime) ? format(plannedStart, 'HH:mm') : '--:--'
  const endLabel = !isNaN(plannedEndTime) ? format(plannedEnd, 'HH:mm') : '--:--'

  return (
    <div
      className={`${styles.container} ${isOvertime ? styles.overtime : ''} ${className || ''}`}
      role="region"
      aria-label="Progress Timeline"
      data-testid="progress-timeline"
      data-overtime={isOvertime}
    >
      <div className={styles.metaRow}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>TIMELINE</span>
          <span className={styles.stats}>
            {stepsDone}/{totalSteps} steps completed ({totalSteps > 0 ? Math.round((stepsDone / totalSteps) * 100) : 0}%)
          </span>
        </div>
        <span
          className={`${styles.statusTag} ${isOvertime ? styles.tagOvertime : styles.tagOnTime}`}
          data-testid="timeline-status-tag"
        >
          {isOvertime ? '⚠️ OVERTIME' : '✓ ON SCHEDULE'}
        </span>
      </div>

      <div className={styles.trackContainer}>
        <div className={styles.track}>
          <div
            className={`${styles.fill} ${isOvertime ? styles.fillOvertime : ''}`}
            style={{ width: `${percent}%` }}
            data-testid="timeline-fill"
          />
        </div>
        <div
          className={`${styles.nowIndicator} ${isOvertime ? styles.nowIndicatorOvertime : ''}`}
          style={{ left: `${percent}%` }}
          title={`Current position: ${percent}%`}
          data-testid="timeline-indicator"
        />
      </div>

      <div className={styles.labelsRow}>
        <span>Start: {startLabel}</span>
        <span>Est: {percent}%</span>
        <span>End: {endLabel}</span>
      </div>
    </div>
  )
}
